import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import './customerstock.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css'; // Ensure this is imported
import DropOffModal from './DropOffModal';
import PickupModal from '../PickupModal'; 
import SalesModal from './SalesModal';
import AddIncidentModal from './AddIncidentModal';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomerStocks = ({ userEmail }) => {
    const { customerId } = useParams();
    const [customer, setCustomer] = useState(null);
    const [stocks, setStocks] = useState([]);
    const [showStocklistModal, setShowStocklistModal] = useState(false);
    const [showPickupModal, setShowPickupModal] = useState(false);
    const [showSalesModal, setShowSalesModal] = useState(false);
    const [showAddIncidentModal, setShowAddIncidentModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [error, setError] = useState(null);
    // Add state for incidents
    const [customerIncidents, setCustomerIncidents] = useState([]);
    const [incidentsLoading, setIncidentsLoading] = useState(false);

    // Fetch customer details
    useEffect(() => {
        axios.get(`${BASE_URL}api/ProspCustomers/GetCustomerDetails?customerID=${customerId}`)
            .then(response => {
                setCustomer(response.data);
            })
            .catch(error => {
                setCustomer(null);
            });
    }, [customerId]);

    // Fetch stock for the customer
    useEffect(() => {
        axios.get(`${BASE_URL}api/StockTransReports/StocksWithOneCustomer?CustomerID=${customerId}`)
            .then(response => {
                setStocks(Array.isArray(response.data) ? response.data : []);
                setError(null);
            })
            .catch(error => {
                setError('Failed to load stocks. Please try again later.');
            });
    }, [customerId]);

    // Fetch customer incidents
    useEffect(() => {
        if (customerId) {
            setIncidentsLoading(true);
            fetch(`${BASE_URL}api/ProspCustomers/GetCustomerIncidents?customerId=${customerId}`)
                .then(response => {
                    return response.ok ? response.json() : Promise.reject('Failed to fetch customer incidents');
                })
                .then(data => {
                    setCustomerIncidents(data);
                })
                .catch(error => {
                })
                .finally(() => setIncidentsLoading(false));
        }
    }, [customerId]);

    if (error) {
        return (
            <div className="container mt-5 text-center">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="container mt-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p>Loading customer details...</p>
            </div>
        );
    }

    const handleDropOffClick = () => {
        setModalTitle("Drop Off");
        setShowStocklistModal(true);
    };

    const handlePickUpClick = () => {
        setShowPickupModal(true);
    };

    const handleSalesClick = () => {
        setShowSalesModal(true);
    };

    const handleAddIncidentClick = () => {
        setShowAddIncidentModal(true);
    };

    const handleCloseStocklistModal = () => {
        setShowStocklistModal(false);
    };

    const handleClosePickupModal = () => {
        setShowPickupModal(false);
    };

    const handleCloseSalesModal = () => {
        setShowSalesModal(false);
    };

    const handleCloseAddIncidentModal = () => {
        setShowAddIncidentModal(false);
    };

    const handleIncidentAdded = () => {
        // Refresh the incidents list after adding a new incident
        if (customerId) {
            setIncidentsLoading(true);
            fetch(`${BASE_URL}api/ProspCustomers/GetCustomerIncidents?customerId=${customerId}`)
                .then(response => {
                    return response.ok ? response.json() : Promise.reject('Failed to fetch customer incidents');
                })
                .then(data => {
                    setCustomerIncidents(data);
                })
                .catch(error => {
                })
                .finally(() => setIncidentsLoading(false));
        }
    };

    return (
        <div className="main-content">
            <div className="container">
                <header className="header">
                    <div className="header-content d-flex justify-content-between align-items-center">
                        <div className="logo">
                            <i className="bi bi-building"></i>
                            <span>{customer.vcmx1}</span>
                        </div>
                    </div>
                    <br/>
                    <div className="row">
                        <div className="header-buttons ms-auto d-flex">
                            <button className="btn btn-warning me-2" onClick={handleAddIncidentClick}>
                                <i className="bi bi-exclamation-triangle me-2"></i> Add Incident
                            </button>
                            <button className="btn btn-primary me-2" onClick={handleDropOffClick}>
                                <i className="bi bi-arrow-down-circle me-2"></i> Drop Off
                            </button>
                            <button className="btn btn-secondary me-2" onClick={handlePickUpClick}>
                                <i className="bi bi-arrow-up-circle me-2"></i> Pick Up
                            </button>
                            <button className="btn btn-success me-2" onClick={handleSalesClick}>
                                <i className="bi bi-cash-coin me-2"></i> Sales
                            </button>
                        </div>
                    </div>
                </header>
                <br/>
                {/* Spacer div to create space between header and Customer Details card */}
                <div className="mobile-spacer" style={{ height: '200px' }}></div>
                <div className="card shadow-sm mb-4 customer-details-card">
                    <div className="card-header bg-primary text-white d-flex flex-wrap justify-content-between align-items-center">
                        <h1 className="card-title m-0"><i className="bi bi-person-circle"></i> Customer Details</h1>
                    </div>
                    <div className="card-body">
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <p><strong>Name:</strong> {customer.vcmx2}</p>
                            </div>
                            <div className="col-md-6">
                                <p><strong>Company:</strong> {customer.vcmx1}</p>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <p><strong>Contact:</strong> {customer.vcmx3}</p>
                            </div>
                            <div className="col-md-6">
                                <p><strong>Address:</strong> {customer.vcmx4}</p>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <p>
                                    <strong>Demo App Installed:</strong>{" "}
                                    {customer.bit1 === true ? "Yes" : customer.bit1 === false ? "No" : "Unknown"}
                                </p>
                                {customer.flt1 != null && customer.flt2 != null && (
                                    <p>
                                        <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${customer.flt1},${customer.flt2}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-outline-primary btn-sm"
                                        >
                                            <i className="bi bi-signpost-2 me-1"></i> Navigate to Office
                                        </a>
                                    </p>
                                )}
                            </div>
                            <div className="col-md-6">
                                {customer.vcmx5 && (
                                    <>
                                        <p className="mb-1"><strong>Office Photo:</strong></p>
                                        <img
                                            src={`${BASE_URL}${customer.vcmx5}`}
                                            alt="Customer office"
                                            style={{ maxWidth: "160px", maxHeight: "160px" }}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <DropOffModal
                    show={showStocklistModal}
                    onHide={handleCloseStocklistModal}
                    title={modalTitle}
                    onConfirm={() => {}}
                    customer={customer}
                    userEmail={userEmail}
                />
                <PickupModal
                    show={showPickupModal}
                    onHide={handleClosePickupModal}
                    onConfirm={() => {}}
                    stocks={stocks}
                    userEmail={userEmail}
                />
                <SalesModal
                    show={showSalesModal}
                    onHide={handleCloseSalesModal}
                    onConfirm={() => {}}
                    stocks={stocks}
                    userEmail={userEmail}
                />
                <AddIncidentModal
                    show={showAddIncidentModal}
                    onHide={handleCloseAddIncidentModal}
                    onConfirm={handleIncidentAdded}
                    userEmail={userEmail}
                />
                <div className="card shadow-sm mb-4">
                    <div className="card-header bg-success text-white d-flex flex-wrap justify-content-between align-items-center">
                        <h2 className="card-title m-0"><i className="bi bi-box-seam"></i> Stock</h2>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            {stocks.length === 0 ? (
                                <div className="alert alert-info text-center m-0">No stock data available for this customer.</div>
                            ) : (
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Code</th>
                                        <th>Description</th>
                                        <th className="text-end">Qty</th>
                                        <th className="text-end">Elapsed Days</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stocks.map(stock => (
                                        <tr key={stock.code}>
                                            <td>{stock.code}</td>
                                            <td>{stock.description}</td>
                                            <td className="text-end">{stock.flt1}</td>
                                            <td className="text-end">{stock.elapsedDays}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            )}
                        </div>
                    </div>
                </div>
                {/* Customer Incidents Card - now styled like the stock card */}
                <div className="card shadow-sm">
                    <div className="card-header bg-info text-white d-flex flex-wrap justify-content-between align-items-center">
                        <h2 className="card-title m-0"><i className="bi bi-exclamation-triangle"></i> Customer Incidents History</h2>
                    </div>
                    <div className="card-body">
                        {incidentsLoading ? (
                            <div className="text-center">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : customerIncidents.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Incident</th>
                                            <th>Incident Type</th>
                                            <th>Doc No.</th>
                                            <th>User</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customerIncidents
                                            .sort((a, b) => {
                                                // Sort by date in descending order (newest first)
                                                const dateA = a.date1 ? new Date(a.date1) : new Date(0);
                                                const dateB = b.date1 ? new Date(b.date1) : new Date(0);
                                                return dateB - dateA;
                                            })
                                            .map((incident, index) => (
                                            <tr key={index}>
                                                <td>{incident.date1 ? new Date(incident.date1).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</td>
                                                <td>{incident.vcmx1 || '-'}</td>
                                                <td>{incident.vc1 || '-'}</td>
                                                <td>{incident.vc2 ? <Link to={`/details/${incident.vc2}`}>{incident.vc2}</Link> : '-'}</td>
                                                <td>{incident.createdBy || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center text-muted">
                                <p>No incidents found for this customer.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerStocks;